// Bulk import service for CSV/Excel file processing

export const bulkImportService = {
  // Parse CSV file content
  parseCSV: (csvContent) => {
    try {
      const lines = csvContent.split('\n').filter(line => line.trim());
      if (lines.length === 0) {
        throw new Error('File is empty');
      }

      // Parse header
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      
      // Validate required headers
      const requiredHeaders = ['State', 'ZipCode', 'TaxRegionName'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      if (missingHeaders.length > 0) {
        throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
      }

      // Parse data rows
      const records = [];
      const errors = [];
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        try {
          const values = bulkImportService.parseCSVLine(line);
          if (values.length !== headers.length) {
            errors.push({
              row: i + 1,
              error: `Column count mismatch. Expected ${headers.length}, got ${values.length}`,
              data: values
            });
            continue;
          }

          const record = {};
          headers.forEach((header, index) => {
            record[header] = values[index];
          });

          // Validate and transform the record
          const validatedRecord = bulkImportService.validateTaxRecord(record, i + 1);
          if (validatedRecord.isValid) {
            records.push(validatedRecord.record);
          } else {
            errors.push({
              row: i + 1,
              error: validatedRecord.errors.join('; '),
              data: record
            });
          }
        } catch (parseError) {
          errors.push({
            row: i + 1,
            error: `Parse error: ${parseError.message}`,
            data: line
          });
        }
      }

      return {
        success: true,
        headers,
        records,
        errors,
        summary: {
          totalRows: lines.length - 1,
          validRecords: records.length,
          errorRecords: errors.length,
          successRate: records.length > 0 ? ((records.length / (records.length + errors.length)) * 100).toFixed(1) : 0
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        headers: [],
        records: [],
        errors: []
      };
    }
  },

  // Parse a single CSV line handling quoted values
  parseCSVLine: (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    values.push(current.trim());
    return values;
  },

  // Validate individual tax record
  validateTaxRecord: (record, rowNumber) => {
    const errors = [];
    const cleanRecord = { ...record };

    // Required field validation
    if (!record.State || record.State.trim() === '') {
      errors.push('State is required');
    } else {
      cleanRecord.State = record.State.trim().toUpperCase();
    }

    if (!record.ZipCode || record.ZipCode.trim() === '') {
      errors.push('ZipCode is required');
    } else {
      const zipCode = record.ZipCode.trim();
      if (!/^\d{5}$/.test(zipCode)) {
        errors.push('ZipCode must be 5 digits');
      }
      cleanRecord.ZipCode = zipCode;
    }

    if (!record.TaxRegionName || record.TaxRegionName.trim() === '') {
      errors.push('TaxRegionName is required');
    } else {
      cleanRecord.TaxRegionName = record.TaxRegionName.trim();
    }

    // Rate validation and conversion
    const rateFields = [
      'EstimatedCombinedRate',
      'StateRate', 
      'EstimatedCountyRate',
      'EstimatedCityRate',
      'EstimatedSpecialRate'
    ];

    rateFields.forEach(field => {
      if (record[field] !== undefined && record[field] !== '') {
        const rate = parseFloat(record[field]);
        if (isNaN(rate)) {
          errors.push(`${field} must be a valid number`);
        } else if (rate < 0 || rate > 1) {
          errors.push(`${field} must be between 0 and 1`);
        } else {
          // Keep as decimal (0.09375 for 9.375%)
          cleanRecord[field] = rate;
        }
      } else {
        cleanRecord[field] = 0;
      }
    });

    // Risk level validation
    if (record.RiskLevel !== undefined && record.RiskLevel !== '') {
      const riskLevel = parseInt(record.RiskLevel);
      if (isNaN(riskLevel)) {
        errors.push('RiskLevel must be a valid integer');
      } else {
        cleanRecord.RiskLevel = riskLevel;
      }
    } else {
      cleanRecord.RiskLevel = 0;
    }

    // Additional business logic validation
    if (cleanRecord.EstimatedCombinedRate && cleanRecord.StateRate) {
      const calculatedTotal = 
        (cleanRecord.StateRate || 0) + 
        (cleanRecord.EstimatedCountyRate || 0) + 
        (cleanRecord.EstimatedCityRate || 0) + 
        (cleanRecord.EstimatedSpecialRate || 0);
      
      const tolerance = 0.0001; // Small tolerance for floating point comparison
      if (Math.abs(calculatedTotal - cleanRecord.EstimatedCombinedRate) > tolerance) {
        errors.push(`Combined rate (${cleanRecord.EstimatedCombinedRate}) doesn't match sum of components (${calculatedTotal.toFixed(4)})`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      record: cleanRecord,
      rowNumber
    };
  },

  // Read file content using FileReader
  readFileContent: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        resolve(event.target.result);
      };
      
      reader.onerror = (error) => {
        reject(new Error(`Failed to read file: ${error.message}`));
      };
      
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reject(new Error('Unsupported file type. Please use CSV files.'));
      }
    });
  },

  // Validate file before processing
  validateFile: (file) => {
    const errors = [];
    
    // File type validation
    const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
    const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                    file.type === 'application/vnd.ms-excel' ||
                    file.name.endsWith('.xlsx') || 
                    file.name.endsWith('.xls');
    
    if (!isCSV && !isExcel) {
      errors.push('File must be CSV or Excel format');
    }
    
    // File size validation (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      errors.push('File size must be less than 10MB');
    }
    
    // File name validation
    if (!file.name || file.name.trim() === '') {
      errors.push('File must have a valid name');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      fileInfo: {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      }
    };
  },

  // Process uploaded file
  processFile: async (file) => {
    try {
      // Validate file first
      const fileValidation = bulkImportService.validateFile(file);
      if (!fileValidation.isValid) {
        return {
          success: false,
          error: fileValidation.errors.join('; '),
          fileInfo: fileValidation.fileInfo
        };
      }

      // Read file content
      const content = await bulkImportService.readFileContent(file);
      
      // Parse content based on file type
      let parseResult;
      if (file.name.endsWith('.csv') || file.type === 'text/csv') {
        parseResult = bulkImportService.parseCSV(content);
      } else {
        // Excel parsing would go here in future
        return {
          success: false,
          error: 'Excel file parsing not yet implemented'
        };
      }

      return {
        ...parseResult,
        fileInfo: fileValidation.fileInfo,
        processedAt: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: `Failed to process file: ${error.message}`
      };
    }
  },

  // Generate jurisdiction analysis
  analyzeJurisdictions: (records) => {
    const jurisdictions = new Map();
    const states = new Set();
    const counties = new Set();
    const cities = new Set();
    const zipCodes = new Set();
    const needsGeocoding = new Map(); // ZIP codes that need geocoding

    records.forEach(record => {
      const { State, ZipCode, TaxRegionName } = record;
      
      states.add(State);
      zipCodes.add(ZipCode);
      cities.add(TaxRegionName);
      
      // Mark ZIP for geocoding if we haven't seen it yet
      if (!needsGeocoding.has(ZipCode)) {
        needsGeocoding.set(ZipCode, {
          state: State,
          city: TaxRegionName,
          zipCode: ZipCode
        });
      }
      
      // Create temporary jurisdiction key (will be updated after geocoding)
      const tempJurisdictionKey = `US_${State}_PENDING_${TaxRegionName.replace(/\s+/g, '_').toUpperCase()}_${ZipCode}`;
      
      if (!jurisdictions.has(tempJurisdictionKey)) {
        jurisdictions.set(tempJurisdictionKey, {
          id: tempJurisdictionKey,
          country: 'US',
          state: State,
          stateName: bulkImportService.getStateName(State),
          county: 'PENDING', // Will be populated by geocoding
          countyCode: '', // Will be populated if available
          city: TaxRegionName,
          cityCode: '', // Optional, can be populated later
          zipCode: ZipCode,
          salesTaxRates: {
            state: record.StateRate || 0,  // Already converted to percentage in validateTaxRecord
            county: record.EstimatedCountyRate || 0,
            city: record.EstimatedCityRate || 0,
            district: record.EstimatedSpecialRate || 0,
            total: record.EstimatedCombinedRate || 0
          },
          records: []
        });
      }
      
      jurisdictions.get(tempJurisdictionKey).records.push(record);
    });

    const geocodingArray = Array.from(needsGeocoding.values());
    console.log(`🔍 Analysis complete: ${geocodingArray.length} ZIP codes marked for geocoding`, geocodingArray.map(z => z.zipCode));
    
    return {
      totalJurisdictions: jurisdictions.size,
      uniqueStates: states.size,
      uniqueCounties: counties.size, // Will be updated after geocoding
      uniqueCities: cities.size,
      uniqueZipCodes: zipCodes.size,
      jurisdictions: Array.from(jurisdictions.values()),
      needsGeocoding: geocodingArray,
      breakdown: {
        states: Array.from(states),
        counties: Array.from(counties),
        cities: Array.from(cities),
        zipCodes: Array.from(zipCodes)
      }
    };
  },

  // Helper to get state full name
  getStateName: (stateCode) => {
    const stateNames = {
      'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
      'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
      'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
      'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
      'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
      'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
      'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
      'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
      'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
      'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
      'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
      'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
      'WI': 'Wisconsin', 'WY': 'Wyoming'
    };
    return stateNames[stateCode] || stateCode;
  },

  // Transform jurisdictions to sales tax rules format - Creates separate documents for each level
  transformToSalesTaxRules: (jurisdictions, effectiveDate = new Date().toISOString()) => {
    const salesTaxRules = [];
    const processedLevels = new Set(); // Track what we've already created
    
    jurisdictions.forEach(jurisdiction => {
      // Create separate tax rule documents for each jurisdiction level
      
      // 1. State level tax rate
      const stateKey = `STATE_${jurisdiction.state}`;
      if (!processedLevels.has(stateKey) && jurisdiction.salesTaxRates.state > 0) {
        salesTaxRules.push({
          id: stateKey,
          jurisdiction: {
            state: jurisdiction.state
          },
          level: 'state',
          taxRate: jurisdiction.salesTaxRates.state,
          effectiveDate: effectiveDate,
          expirationDate: null,
          isActive: true,
          precedence: 1000,
          dataSource: 'csv_import',
          lastVerified: new Date().toISOString(),
          createdBy: 'bulk_import',
          source: 'bulk_import'
        });
        processedLevels.add(stateKey);
      }
      
      // 2. County level tax rate
      if (jurisdiction.county && jurisdiction.county !== 'PENDING' && jurisdiction.county !== 'UNKNOWN') {
        const countyKey = `COUNTY_${jurisdiction.state}_${jurisdiction.county}`;
        if (!processedLevels.has(countyKey) && jurisdiction.salesTaxRates.county > 0) {
          salesTaxRules.push({
            id: countyKey,
            jurisdiction: {
              state: jurisdiction.state,
              county: jurisdiction.county
            },
            level: 'county',
            taxRate: jurisdiction.salesTaxRates.county,
            effectiveDate: effectiveDate,
            expirationDate: null,
            isActive: true,
            precedence: 1000,
            dataSource: 'csv_import',
            lastVerified: new Date().toISOString(),
            createdBy: 'bulk_import',
            source: 'bulk_import'
          });
          processedLevels.add(countyKey);
        }
      }
      
      // 3. City level tax rate
      const cityKey = `CITY_${jurisdiction.state}_${jurisdiction.county}_${jurisdiction.city}`;
      if (!processedLevels.has(cityKey) && jurisdiction.salesTaxRates.city > 0) {
        salesTaxRules.push({
          id: cityKey,
          jurisdiction: {
            state: jurisdiction.state,
            county: jurisdiction.county !== 'PENDING' && jurisdiction.county !== 'UNKNOWN' 
              ? jurisdiction.county 
              : 'Unknown County',
            city: jurisdiction.city
          },
          level: 'city',
          taxRate: jurisdiction.salesTaxRates.city,
          effectiveDate: effectiveDate,
          expirationDate: null,
          isActive: true,
          precedence: 1000,
          dataSource: 'csv_import',
          lastVerified: new Date().toISOString(),
          createdBy: 'bulk_import',
          source: 'bulk_import'
        });
        processedLevels.add(cityKey);
      }
      
      // 4. ZIP code level tax rate (stores the total combined rate)
      salesTaxRules.push({
        id: `ZIP_${jurisdiction.state}_${jurisdiction.county}_${jurisdiction.city}_${jurisdiction.zipCode}`,
        jurisdiction: {
          state: jurisdiction.state,
          county: jurisdiction.county !== 'PENDING' && jurisdiction.county !== 'UNKNOWN' 
            ? jurisdiction.county 
            : 'Unknown County',
          city: jurisdiction.city,
          zipCode: jurisdiction.zipCode
        },
        level: 'zipcode',
        taxRate: jurisdiction.salesTaxRates.total, // Total combined rate for the ZIP code
        effectiveDate: effectiveDate,
        expirationDate: null,
        isActive: true,
        precedence: 1000,
        dataSource: 'csv_import',
        lastVerified: new Date().toISOString(),
        createdBy: 'bulk_import',
        source: 'bulk_import'
      });
    });
    
    return salesTaxRules;
  },

  // Format validation results for display
  formatValidationSummary: (parseResult) => {
    const { summary, errors } = parseResult;
    
    return {
      stats: [
        { label: 'Total Rows', value: summary.totalRows, type: 'info' },
        { label: 'Valid Records', value: summary.validRecords, type: 'success' },
        { label: 'Error Records', value: summary.errorRecords, type: 'error' },
        { label: 'Success Rate', value: `${summary.successRate}%`, type: 'info' }
      ],
      errors: errors.slice(0, 10), // Show first 10 errors
      hasMoreErrors: errors.length > 10
    };
  },

  // Bulk geocoding with progress tracking
  bulkGeocode: async (zipCodesToGeocode, onProgress = () => {}) => {
    const { jurisdictionsService } = await import('../services/jurisdictionsService');
    const results = new Map();
    const batchSize = 10;
    const totalZipCodes = zipCodesToGeocode.length;
    let processed = 0;
    
    console.log(`🚀 Starting bulk geocoding for ${totalZipCodes} ZIP codes:`, zipCodesToGeocode.map(z => z.zipCode));
    
    // Process in batches of 10
    for (let i = 0; i < zipCodesToGeocode.length; i += batchSize) {
      const batch = zipCodesToGeocode.slice(i, i + batchSize);
      
      // Process batch concurrently
      const batchPromises = batch.map(async (zipData) => {
        try {
          const geocodeResult = await jurisdictionsService.geocodeZipCode(zipData.zipCode);
          
          if (geocodeResult.success && geocodeResult.data) {
            return {
              zipCode: zipData.zipCode,
              success: true,
              county: geocodeResult.data.county || 'UNKNOWN',
              countyCode: '', // Not provided by Google geocoding
              state: geocodeResult.data.state,
              city: geocodeResult.data.city,
              coordinates: geocodeResult.data.coordinates,
              source: 'google_geocoding'
            };
          } else {
            return {
              zipCode: zipData.zipCode,
              success: false,
              county: 'UNKNOWN',
              countyCode: '',
              state: zipData.state,
              city: zipData.city,
              coordinates: null,
              source: 'failed_geocoding',
              error: geocodeResult.message || 'Unknown error'
            };
          }
        } catch (error) {
          return {
            zipCode: zipData.zipCode,
            success: false,
            county: 'UNKNOWN',
            countyCode: '',
            state: zipData.state,
            city: zipData.city,
            coordinates: null,
            source: 'failed_geocoding',
            error: error.message
          };
        }
      });
      
      // Wait for batch to complete
      const batchResults = await Promise.all(batchPromises);
      
      // Store results
      batchResults.forEach(result => {
        results.set(result.zipCode, result);
      });
      
      // Update progress
      processed += batchResults.length;
      const progressPercent = Math.round((processed / totalZipCodes) * 100);
      onProgress({
        processed,
        total: totalZipCodes,
        percent: progressPercent,
        currentBatch: Math.ceil((i + batchSize) / batchSize),
        totalBatches: Math.ceil(totalZipCodes / batchSize),
        recentResults: batchResults
      });
      
      // Add delay between batches to respect API rate limits
      if (i + batchSize < zipCodesToGeocode.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    return results;
  },

  // Update jurisdictions with geocoding results
  updateJurisdictionsWithGeocoding: (jurisdictions, geocodingResults) => {
    return jurisdictions.map(jurisdiction => {
      const geocodeResult = geocodingResults.get(jurisdiction.zipCode);
      
      if (geocodeResult) {
        // Update jurisdiction ID to use real county name
        const updatedId = `US_${jurisdiction.state}_${geocodeResult.county.replace(/\s+/g, '_').toUpperCase()}_${jurisdiction.city.replace(/\s+/g, '_').toUpperCase()}_${jurisdiction.zipCode}`;
        
        return {
          ...jurisdiction,
          id: updatedId,
          county: geocodeResult.county,
          countyCode: geocodeResult.countyCode || '',
          coordinates: geocodeResult.coordinates,
          dataSource: geocodeResult.source,
          geocodingStatus: geocodeResult.success ? 'success' : 'failed',
          geocodingError: geocodeResult.error || null
        };
      }
      
      return jurisdiction;
    });
  },

  // Detect conflicts between existing and new sales tax rules
  detectConflicts: async (salesTaxRules) => {
    const { salesTaxRulesService } = await import('../services/salesTaxRulesService');
    const conflicts = [];
    const newRules = [];
    
    for (const rule of salesTaxRules) {
      try {
        const existingRule = await salesTaxRulesService.getRule(rule.id);
        
        if (existingRule) {
          // Check for meaningful differences
          const taxRateDifferent = Math.abs((existingRule.taxRate || 0) - rule.taxRate) > 0.0001;
          const effectiveDateDifferent = existingRule.effectiveDate !== rule.effectiveDate;
          
          if (taxRateDifferent || effectiveDateDifferent) {
            conflicts.push({
              id: rule.id,
              level: rule.level,
              jurisdiction: rule.jurisdiction,
              existing: {
                taxRate: existingRule.taxRate || 0,
                effectiveDate: existingRule.effectiveDate,
                updatedAt: existingRule.updatedAt
              },
              new: {
                taxRate: rule.taxRate,
                effectiveDate: rule.effectiveDate
              },
              differences: {
                taxRate: taxRateDifferent,
                effectiveDate: effectiveDateDifferent
              }
            });
          }
        } else {
          // This is a completely new rule
          newRules.push({
            id: rule.id,
            level: rule.level,
            jurisdiction: rule.jurisdiction,
            taxRate: rule.taxRate,
            effectiveDate: rule.effectiveDate
          });
        }
      } catch (error) {
        console.error(`Error checking rule ${rule.id}:`, error);
      }
    }
    
    return {
      conflicts,
      newRules,
      totalRules: salesTaxRules.length,
      hasConflicts: conflicts.length > 0,
      summary: {
        existing: conflicts.length,
        new: newRules.length,
        total: salesTaxRules.length
      }
    };
  },

  // Prepare import preview data
  prepareImportPreview: (parseResult) => {
    if (!parseResult.success || parseResult.records.length === 0) {
      return null;
    }
    
    // Analyze jurisdictions
    const jurisdictionAnalysis = bulkImportService.analyzeJurisdictions(parseResult.records);
    
    // Transform to sales tax rules
    const salesTaxRules = bulkImportService.transformToSalesTaxRules(
      jurisdictionAnalysis.jurisdictions
    );
    
    return {
      summary: {
        totalRecords: parseResult.records.length,
        jurisdictionsToCreate: jurisdictionAnalysis.totalJurisdictions,
        salesTaxRulesToCreate: salesTaxRules.length,
        uniqueStates: jurisdictionAnalysis.uniqueStates,
        uniqueCities: jurisdictionAnalysis.uniqueCities,
        uniqueZipCodes: jurisdictionAnalysis.uniqueZipCodes,
        needsGeocoding: jurisdictionAnalysis.needsGeocoding.length
      },
      jurisdictions: jurisdictionAnalysis.jurisdictions,
      salesTaxRules: salesTaxRules,
      needsGeocoding: jurisdictionAnalysis.needsGeocoding,
      breakdown: jurisdictionAnalysis.breakdown
    };
  },

  // Build hierarchical jurisdiction analysis
  buildJurisdictionHierarchy: (updatedJurisdictions) => {
    const hierarchy = {
      states: new Map(),
      counties: new Map(),
      cities: new Map(),
      zipCodes: new Map(),
      conflicts: []
    };
    
    // Process each jurisdiction to build hierarchy
    updatedJurisdictions.forEach(jurisdiction => {
      const { country, state, county, city, zipCode } = jurisdiction;
      
      // Build state level
      const stateId = `${country}_${state}`;
      if (!hierarchy.states.has(stateId)) {
        hierarchy.states.set(stateId, {
          id: stateId,
          country,
          state,
          stateName: bulkImportService.getStateName(state),
          county: '',
          city: '',
          zipCode: '',
          zipCodes: [],
          parentJurisdictionId: null,
          level: 'state',
          source: 'csv_import'
        });
      }
      
      // Build county level - include UNKNOWN counties to capture failed geocoding
      if (county) {
        const countyId = `${country}_${state}_${county.replace(/\s+/g, '_').toUpperCase()}`;
        if (!hierarchy.counties.has(countyId)) {
          hierarchy.counties.set(countyId, {
            id: countyId,
            country,
            state,
            stateName: bulkImportService.getStateName(state),
            county,
            city: '',
            zipCode: '',
            zipCodes: [],
            parentJurisdictionId: stateId,
            level: 'county',
            source: 'csv_import',
            // Mark UNKNOWN counties for potential geocoding improvement
            needsGeocoding: county === 'UNKNOWN'
          });
        }
        
        // Check for county conflicts within same city
        const existingCounty = hierarchy.counties.get(countyId);
        if (existingCounty && existingCounty.county !== county) {
          hierarchy.conflicts.push({
            type: 'county_mismatch',
            city,
            zipCode,
            existing: existingCounty.county,
            new: county,
            jurisdictionId: jurisdiction.id
          });
        }
      }
      
      // Build city level
      if (city) {
        const countyPart = county 
          ? county.replace(/\s+/g, '_').toUpperCase()
          : 'UNKNOWN';
        const cityId = `${country}_${state}_${countyPart}_${city.replace(/\s+/g, '_').toUpperCase()}`;
        
        if (!hierarchy.cities.has(cityId)) {
          // Always use county as parent, even if UNKNOWN
          const parentCountyId = `${country}_${state}_${countyPart}`;
            
          hierarchy.cities.set(cityId, {
            id: cityId,
            country,
            state,
            stateName: bulkImportService.getStateName(state),
            county: county || 'UNKNOWN',
            city,
            zipCode: '',
            zipCodes: [],
            parentJurisdictionId: parentCountyId,
            level: 'city',
            source: 'csv_import',
            // Mark cities in UNKNOWN counties for potential geocoding improvement
            needsGeocoding: (county === 'UNKNOWN' || !county)
          });
        }
      }
      
      // Build ZIP code level (individual records)
      if (zipCode) {
        const zipId = jurisdiction.id; // Use the full jurisdiction ID
        const countyPart = county 
          ? county.replace(/\s+/g, '_').toUpperCase()
          : 'UNKNOWN';
        
        hierarchy.zipCodes.set(zipId, {
          ...jurisdiction,
          parentJurisdictionId: city 
            ? `${country}_${state}_${countyPart}_${city.replace(/\s+/g, '_').toUpperCase()}`
            : `${country}_${state}_${countyPart}`, // If no city, parent is county (even if UNKNOWN)
          level: 'zipcode',
          zipCodes: [zipCode] // Individual ZIP code in array
        });
      }
    });
    
    return hierarchy;
  },

  // Create jurisdictions in hierarchical order
  createJurisdictionsHierarchy: async (hierarchy, onProgress = () => {}) => {
    const { jurisdictionsService } = await import('../services/jurisdictionsService');
    const results = {
      created: [],
      existing: [],
      errors: [],
      conflicts: hierarchy.conflicts
    };
    
    const totalToCreate = hierarchy.states.size + hierarchy.counties.size + 
                         hierarchy.cities.size + hierarchy.zipCodes.size;
    let created = 0;
    
    try {
      // Step 1: Create states
      onProgress({
        step: 'creating_states',
        message: `Creating ${hierarchy.states.size} state jurisdictions...`,
        percent: 10
      });
      
      for (const [stateId, stateData] of hierarchy.states) {
        try {
          // Check if already exists
          const existing = await jurisdictionsService.getJurisdiction(stateId);
          if (existing) {
            results.existing.push({ id: stateId, level: 'state' });
          } else {
            const created = await jurisdictionsService.createJurisdiction(stateData);
            results.created.push({ id: stateId, level: 'state', data: created });
          }
        } catch (error) {
          results.errors.push({
            id: stateId,
            level: 'state',
            error: error.message
          });
        }
        created++;
        
        onProgress({
          step: 'creating_states',
          message: `Created ${created} of ${totalToCreate} jurisdictions`,
          percent: 10 + Math.round((created / totalToCreate) * 60)
        });
      }
      
      // Step 2: Create counties
      onProgress({
        step: 'creating_counties',
        message: `Creating ${hierarchy.counties.size} county jurisdictions...`,
        percent: 30
      });
      
      for (const [countyId, countyData] of hierarchy.counties) {
        try {
          // Verify parent exists
          const parentExists = await jurisdictionsService.getJurisdiction(countyData.parentJurisdictionId);
          if (!parentExists) {
            results.errors.push({
              id: countyId,
              level: 'county',
              error: `Parent jurisdiction ${countyData.parentJurisdictionId} not found`
            });
            continue;
          }
          
          // Check if already exists
          const existing = await jurisdictionsService.getJurisdiction(countyId);
          if (existing) {
            results.existing.push({ id: countyId, level: 'county' });
          } else {
            const created = await jurisdictionsService.createJurisdiction(countyData);
            results.created.push({ id: countyId, level: 'county', data: created });
          }
        } catch (error) {
          results.errors.push({
            id: countyId,
            level: 'county',
            error: error.message
          });
        }
        created++;
        
        onProgress({
          step: 'creating_counties',
          message: `Created ${created} of ${totalToCreate} jurisdictions`,
          percent: 10 + Math.round((created / totalToCreate) * 60)
        });
      }
      
      // Step 3: Create cities
      onProgress({
        step: 'creating_cities',
        message: `Creating ${hierarchy.cities.size} city jurisdictions...`,
        percent: 50
      });
      
      for (const [cityId, cityData] of hierarchy.cities) {
        try {
          // Verify parent exists
          const parentExists = await jurisdictionsService.getJurisdiction(cityData.parentJurisdictionId);
          if (!parentExists) {
            results.errors.push({
              id: cityId,
              level: 'city',
              error: `Parent jurisdiction ${cityData.parentJurisdictionId} not found`
            });
            continue;
          }
          
          // Check if already exists
          const existing = await jurisdictionsService.getJurisdiction(cityId);
          if (existing) {
            results.existing.push({ id: cityId, level: 'city' });
          } else {
            const created = await jurisdictionsService.createJurisdiction(cityData);
            results.created.push({ id: cityId, level: 'city', data: created });
          }
        } catch (error) {
          results.errors.push({
            id: cityId,
            level: 'city',
            error: error.message
          });
        }
        created++;
        
        onProgress({
          step: 'creating_cities',
          message: `Created ${created} of ${totalToCreate} jurisdictions`,
          percent: 10 + Math.round((created / totalToCreate) * 60)
        });
      }
      
      // Step 4: Create ZIP codes
      onProgress({
        step: 'creating_zipcodes',
        message: `Creating ${hierarchy.zipCodes.size} ZIP code jurisdictions...`,
        percent: 70
      });
      
      for (const [zipId, zipData] of hierarchy.zipCodes) {
        try {
          // Verify parent exists
          const parentExists = await jurisdictionsService.getJurisdiction(zipData.parentJurisdictionId);
          if (!parentExists) {
            results.errors.push({
              id: zipId,
              level: 'zipcode',
              error: `Parent jurisdiction ${zipData.parentJurisdictionId} not found`
            });
            continue;
          }
          
          // Check if already exists
          const existing = await jurisdictionsService.getJurisdiction(zipId);
          if (existing) {
            results.existing.push({ id: zipId, level: 'zipcode' });
          } else {
            const created = await jurisdictionsService.createJurisdiction(zipData);
            results.created.push({ id: zipId, level: 'zipcode', data: created });
          }
        } catch (error) {
          results.errors.push({
            id: zipId,
            level: 'zipcode',
            error: error.message
          });
        }
        created++;
        
        onProgress({
          step: 'creating_zipcodes',
          message: `Created ${created} of ${totalToCreate} jurisdictions`,
          percent: 10 + Math.round((created / totalToCreate) * 60)
        });
      }
      
      onProgress({
        step: 'hierarchy_complete',
        message: 'Jurisdiction hierarchy creation completed',
        percent: 70
      });
      
    } catch (error) {
      results.errors.push({
        id: 'general',
        level: 'system',
        error: error.message
      });
    }
    
    return results;
  },

  // Execute full import process with geocoding and hierarchy
  executeImport: async (parseResult, effectiveDate, conflictResolution = {}, onProgress = () => {}) => {
    const startTime = Date.now();
    
    try {
      // Step 1: Prepare initial data
      const importPreview = bulkImportService.prepareImportPreview(parseResult);
      if (!importPreview) {
        throw new Error('Failed to prepare import preview');
      }
      
      onProgress({
        step: 'preparation',
        message: 'Preparing import data...',
        percent: 5
      });
      
      // Step 2: Geocode ZIP codes FIRST (before checking existing jurisdictions)
      let updatedJurisdictions = importPreview.jurisdictions;
      
      console.log(`📍 ZIP codes needing geocoding: ${importPreview.needsGeocoding.length}`, importPreview.needsGeocoding);
      
      if (importPreview.needsGeocoding.length > 0) {
        onProgress({
          step: 'geocoding',
          message: 'Checking addresses with Google Maps...',
          percent: 8
        });
        
        const geocodingResults = await bulkImportService.bulkGeocode(
          importPreview.needsGeocoding,
          (progress) => {
            onProgress({
              step: 'geocoding',
              message: `Checking addresses with Google Maps: ${progress.processed}/${progress.total}`,
              percent: 8 + Math.round((progress.percent * 0.25))
            });
          }
        );
        
        // Update jurisdictions with geocoding results
        updatedJurisdictions = bulkImportService.updateJurisdictionsWithGeocoding(
          importPreview.jurisdictions,
          geocodingResults
        );
        
        onProgress({
          step: 'geocoding',
          message: 'Address verification completed',
          percent: 35
        });
      }
      
      // Step 3: Build jurisdiction hierarchy with geocoded data
      onProgress({
        step: 'hierarchy_analysis',
        message: 'Analyzing jurisdiction hierarchy...',
        percent: 40
      });
      
      const hierarchy = bulkImportService.buildJurisdictionHierarchy(updatedJurisdictions);
      
      // Step 4: Create jurisdictions in hierarchical order (now using geocoded data)
      const jurisdictionResults = await bulkImportService.createJurisdictionsHierarchy(
        hierarchy,
        (progress) => {
          // Adjust progress percentages since geocoding was moved earlier
          const adjustedPercent = 40 + Math.round((progress.percent - 10) * 0.35);
          onProgress({
            step: progress.step,
            message: progress.message,
            percent: adjustedPercent
          });
        }
      );
      
      // Step 5: Transform sales tax rules with updated effective date
      onProgress({
        step: 'transformation',
        message: 'Preparing sales tax rules...',
        percent: 80
      });
      
      const salesTaxRules = bulkImportService.transformToSalesTaxRules(
        updatedJurisdictions,
        effectiveDate.toISOString()
      );
      
      // Step 6: Save sales tax rules to Firestore
      onProgress({
        step: 'saving_rules',
        message: `Saving ${salesTaxRules.length} sales tax rules...`,
        percent: 85
      });
      
      const salesTaxResults = await bulkImportService.saveSalesTaxRules(
        salesTaxRules,
        conflictResolution,
        (progress) => {
          onProgress({
            step: 'saving_rules',
            message: `Saving sales tax rules: ${progress.processed}/${progress.total}`,
            percent: 85 + Math.round((progress.percent * 0.10))
          });
        }
      );
      
      onProgress({
        step: 'completed',
        message: 'Import process completed successfully',
        percent: 100
      });
      
      const endTime = Date.now();
      const processingTimeMs = endTime - startTime;
      const processingTime = processingTimeMs < 1000 ? 
        `${processingTimeMs}ms` : 
        `${Math.round(processingTimeMs / 1000)}s`;
      
      return {
        success: true,
        jurisdictions: updatedJurisdictions,
        salesTaxRules: salesTaxRules,
        salesTaxResults: salesTaxResults,
        hierarchy: hierarchy,
        jurisdictionResults: jurisdictionResults,
        totalRecords: parseResult.records.length,
        processingTime: processingTime,
        summary: {
          ...importPreview.summary,
          geocodingSuccessful: updatedJurisdictions.filter(j => j.geocodingStatus === 'success').length,
          geocodingFailed: updatedJurisdictions.filter(j => j.geocodingStatus === 'failed').length,
          jurisdictionsCreated: jurisdictionResults.created.length,
          jurisdictionsExisting: jurisdictionResults.existing.length,
          jurisdictionErrors: jurisdictionResults.errors.length,
          conflicts: jurisdictionResults.conflicts.length,
          salesTaxRulesCreated: salesTaxResults.created.length,
          salesTaxRulesUpdated: salesTaxResults.updated.length,
          salesTaxRulesSkipped: salesTaxResults.skipped.length,
          salesTaxRulesErrors: salesTaxResults.errors.length,
          processingTime: processingTime
        }
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message,
        jurisdictions: [],
        salesTaxRules: []
      };
    }
  },

  // Save sales tax rules to Firestore with conflict resolution
  saveSalesTaxRules: async (salesTaxRules, conflictResolution = {}, onProgress = () => {}) => {
    const { salesTaxRulesService } = await import('../services/salesTaxRulesService');
    const results = {
      created: [],
      updated: [],
      skipped: [],
      errors: []
    };
    
    const totalRules = salesTaxRules.length;
    let processed = 0;
    
    // Helper function to format jurisdiction description
    const getJurisdictionDescription = (jurisdiction) => {
      if (jurisdiction.zipCode) {
        return `${jurisdiction.state}-${jurisdiction.zipCode}`;
      } else if (jurisdiction.city) {
        return `${jurisdiction.state}-${jurisdiction.county}-${jurisdiction.city}`;
      } else if (jurisdiction.county) {
        return `${jurisdiction.state}-${jurisdiction.county}`;
      } else {
        return jurisdiction.state;
      }
    };
    
    // Process in batches to avoid overwhelming Firestore
    const batchSize = 50;
    
    for (let i = 0; i < salesTaxRules.length; i += batchSize) {
      const batch = salesTaxRules.slice(i, i + batchSize);
      
      // Process batch
      for (const rule of batch) {
        try {
          // Check if rule already exists
          const existingRule = await salesTaxRulesService.getRule(rule.id);
          
          if (existingRule) {
            // Extract existing tax rate (handle both old and new formats)
            let existingTaxRate = existingRule.taxRate;
            if (existingTaxRate === undefined || existingTaxRate === null) {
              // Fallback to old format
              if (existingRule.rateConfig && existingRule.rateConfig.totalRate !== undefined) {
                existingTaxRate = existingRule.rateConfig.totalRate;
              } else {
                existingTaxRate = 0;
              }
            }
            
            // Check for differences
            const taxRateDifferent = Math.abs(existingTaxRate - rule.taxRate) > 0.0001;
            const effectiveDateDifferent = existingRule.effectiveDate !== rule.effectiveDate;
            const hasDifferences = taxRateDifferent || effectiveDateDifferent;
            
            // Log for debugging
            console.log(`📊 Comparing rule ${rule.id}:`, {
              existingTaxRate,
              newTaxRate: rule.taxRate,
              taxRateDifferent,
              effectiveDateDifferent,
              hasDifferences,
              conflictResolution: conflictResolution[rule.id] || conflictResolution.bulkAction || 'none'
            });
            
            if (hasDifferences) {
              // Apply conflict resolution strategy
              const resolution = conflictResolution[rule.id] || conflictResolution.bulkAction || 'skip';
              
              if (resolution === 'update' || resolution === 'updateIfDifferent') {
                console.log(`✅ Updating rule ${rule.id} with archiving...`);
                // Update existing rule with archiving
                await salesTaxRulesService.updateRuleWithArchive(rule.id, {
                  taxRate: rule.taxRate,
                  effectiveDate: rule.effectiveDate,
                  lastVerified: new Date().toISOString()
                }, 'bulk_import');
                
                results.updated.push({ 
                  id: rule.id, 
                  jurisdiction: getJurisdictionDescription(rule.jurisdiction),
                  changes: {
                    taxRate: taxRateDifferent ? { from: existingRule.taxRate, to: rule.taxRate } : null,
                    effectiveDate: effectiveDateDifferent ? { from: existingRule.effectiveDate, to: rule.effectiveDate } : null
                  }
                });
              } else {
                // Skip existing rule
                results.skipped.push({ 
                  id: rule.id, 
                  jurisdiction: getJurisdictionDescription(rule.jurisdiction),
                  reason: 'user_chose_skip'
                });
              }
            } else {
              // No differences, just mark as skipped
              results.skipped.push({ 
                id: rule.id, 
                jurisdiction: getJurisdictionDescription(rule.jurisdiction),
                reason: 'no_changes'
              });
            }
          } else {
            // Create new rule
            const createdRule = await salesTaxRulesService.createRule(rule);
            results.created.push({ 
              id: createdRule.id, 
              jurisdiction: getJurisdictionDescription(rule.jurisdiction)
            });
          }
        } catch (error) {
          results.errors.push({
            id: rule.id,
            jurisdiction: getJurisdictionDescription(rule.jurisdiction),
            error: error.message
          });
        }
        
        processed++;
        
        // Update progress
        const progressPercent = Math.round((processed / totalRules) * 100);
        onProgress({
          processed,
          total: totalRules,
          percent: progressPercent,
          step: 'saving_rules'
        });
      }
      
      // Add small delay between batches to avoid rate limits
      if (i + batchSize < salesTaxRules.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    
    return results;
  }
};

export default bulkImportService;